import React from "react";
import { Box, MenuItem, Paper, TextField, Typography } from "@mui/material";
import type { SpiritualOption, TeamLeadershipAssignment } from "./teamFormationTypes";
import {
  teamOverlineSx,
  teamPanelHeadingSx,
  teamPanelSubtextSx,
  TEAM_FORMATION_FONT,
} from "./teamFormationUi";

type Props = {
  assignments: TeamLeadershipAssignment[];
  spiritualMentorOptions: SpiritualOption[];
  spiritualDirectorOptions: SpiritualOption[];
  onAssign: (role: string, principalId: string | number | null) => void;
};

export default function TeamLeadershipPanel({
  assignments,
  spiritualMentorOptions,
  spiritualDirectorOptions,
  onAssign,
}: Props) {
  return (
    <Paper component="section" variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
      <Typography component="span" sx={teamOverlineSx}>
        Team leadership
      </Typography>
      <Typography sx={teamPanelHeadingSx}>Retreat Team Leadership</Typography>
      <Typography sx={{ ...teamPanelSubtextSx, mt: 0.5 }}>
        Internal team assignments are managed here and do not affect publication
        readiness.
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(3, minmax(0, 1fr))",
          },
          gap: 1,
          mt: 1.5,
        }}
      >
        {assignments.map((assignment) => {
          const options =
            assignment.role === "SPIRITUAL_MENTOR_COUPLE"
              ? spiritualMentorOptions
              : assignment.role === "SPIRITUAL_DIRECTOR"
                ? spiritualDirectorOptions
                : [];

          return (
            <Paper
              key={assignment.role}
              variant="outlined"
              sx={{ p: 1.25, minWidth: 0 }}
            >
              {assignment.editable ? (
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={assignment.label}
                  value={assignment.principalId ?? ""}
                  onChange={(e) =>
                    onAssign(
                      assignment.role,
                      e.target.value === "" ? null : e.target.value,
                    )
                  }
                  helperText={
                    assignment.principalType === "CLERGY"
                      ? "Select clergy"
                      : "Select a couple"
                  }
                  sx={{
                    "& .MuiInputLabel-root": {
                      fontFamily: TEAM_FORMATION_FONT,
                      fontSize: "0.8125rem",
                    },
                    "& .MuiInputBase-root": {
                      fontFamily: TEAM_FORMATION_FONT,
                      fontSize: "0.8125rem",
                    },
                    "& .MuiFormHelperText-root": {
                      fontFamily: TEAM_FORMATION_FONT,
                      fontSize: "0.75rem",
                    },
                  }}
                >
                  <MenuItem value="">Not assigned</MenuItem>
                  {options.map((opt) => (
                    <MenuItem key={opt.principalId} value={opt.principalId}>
                      {opt.displayName}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <>
                  <Typography
                    sx={{
                      fontFamily: TEAM_FORMATION_FONT,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                      lineHeight: 1.4,
                    }}
                  >
                    {assignment.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: TEAM_FORMATION_FONT,
                      fontSize: "0.875rem",
                      fontWeight: 700,
                      lineHeight: 1.35,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {assignment.displayName ?? "Not assigned"}
                  </Typography>
                </>
              )}
            </Paper>
          );
        })}
      </Box>
    </Paper>
  );
}
